import React, { useMemo } from 'react'
import { Stack } from '@mui/material'
import ScrollTable from 'components/global/CUK/ScrollTable'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'

import CONFIG_COLUMNS, { ExpandedRowDetails } from './config-columns'
import { useParams } from 'react-router-dom'
import TableView, { useTable } from 'packages/ui/Table'
import { useResponsiveColumns } from 'packages/ui/Table'
import { useNavigate } from 'router'
import useFilters, { useContactsSelected, useSort } from '../../store'
import { useSidebar } from 'components/AuthenticatedLayout/Sidebar/SidebarContext'
import { toast } from 'components/global/CUK/Toast/Toast'
import { useTranslation } from 'react-i18next'
import { trackEvent } from 'analytics'

const RESPONSIVE_COLUMNS_CONFIG = {
  mobile: ['check', 'availability', 'name', 'email', 'actions'],
  tablet: ['check', 'availability', 'name', 'email', 'sites', 'actions'],
  desktop: ['check', 'availability', 'name', 'email', 'phone', 'sites', 'last_connexions', 'creation_date'],
  desktopLarge: ['check', 'availability', 'name', 'email', 'phone', 'sites', 'last_connexions', 'creation_date']
}

const LIMIT_SYNC_CONTACTS = 100

const Table = ({ apiUsers, isInternal }) => {
  const { t } = useTranslation()
  const { sfId } = useParams()
  const [sort, setSort] = useSort()
  const [contactsSelected, setContactsSelected] = useContactsSelected()
  const navigate = useNavigate()
  const [{sync}] = useFilters()
  const { expanded: sidebarExpanded } = useSidebar()
  
  const { visibleColumns, getVisibleColumns } = useResponsiveColumns(
    RESPONSIVE_COLUMNS_CONFIG,
    {
      sidebarExpanded,
      excludeColumns: sfId ? ['creation_date', 'phone', 'last_connexions'] : []
    }
  )

  const columns = useMemo(() => {
    const baseColumns = CONFIG_COLUMNS.filter(({name}) => {
      if (name === 'check') {
        return true
      } else {
        return !sfId || (sfId && !['creation_date'].includes(name))
      }
    })
    
    return baseColumns.filter(({ name }) => visibleColumns.includes(name))
  }, [sfId, visibleColumns, sync])

  const responsiveConfig = useMemo(() => ({
    expandedRowRenderer: {
      component: ExpandedRowDetails,
      shouldRender: (screenSize) => screenSize === 'mobile',
      getVisibleColumns
    }
  }), [getVisibleColumns])

  const handleRowClick = (contact) => {
    const isForSync = sync.length === 1 && sync.includes('not_sync')
    
    if (contactsSelected.length > 0) {
      const isSelected = contactsSelected.some(selected => selected.sf_id === contact.sf_id)
      
      if (isForSync) {
        const isSelectable = !contact.sync
        if (isSelectable) {
          if (isSelected) {
            setContactsSelected(prev => prev.filter(selected => selected.sf_id !== contact.sf_id))
          } else {
            if (contactsSelected.length === LIMIT_SYNC_CONTACTS) {
              toast.error(t('auth.card_in_mass.sync.limit_exceed'))
            } else {
              setContactsSelected(prev => [...prev, contact])
            }
          }
        }
      } else {
        const isSelectable = contact.sync
        if (isSelectable) {
          if (isSelected) {
            setContactsSelected(prev => prev.filter(selected => selected.sf_id !== contact.sf_id))
          } else {
            setContactsSelected(prev => [...prev, contact])
          }
        }
      }
    } else {
      trackEvent('user_details')
      navigate({ pathname: `${contact.sf_id}` })
    }
  }

  const apiTable = useTable({
    rowKey: 'sf_id',
    loading: apiUsers.loading,
    loadingNext: apiUsers.loadingNext,
    items: apiUsers.users ? apiUsers.users : [],
    columns,
    onRowClick: handleRowClick,
    rowIsBold: (contact) => {
      return contactsSelected.some(selected => selected.sf_id === contact.sf_id)
    },
    responsiveConfig
  })

  return (
    <Stack
      sx={{
        overflow: 'hidden',
        overflowY: 'auto',
        flex: '1 1 auto',
        scrollbarWidth: 'none',
        height: '100%',
        '&::-webkit-scrollbar ': {
          display: 'none',
        },
      }}
    >
      <ScrollTable
        fetchNextPage={apiUsers.fetchNextPage}
        shouldFetchNextPage={apiUsers.shouldFetchNextPage}
        collapse={sfId}
      >
        {apiUsers.loading ? (
          <TableSkeleton />
        ) : (
          <TableView
            apiTable={apiTable}
            isInternal={isInternal}
            sort={{
              ...sort, 
              onChange: (name) => {
                const column = columns.find(column => column.name === name)
                setSort(column.sortBy || column.name)
              }
            }}
          />
        )}
      </ScrollTable>
    </Stack>
  )
}

export default Table
